import React, { useEffect, useState } from "react";
import {
  AccessTime,
  MonetizationOn,
  EventNote,
  Announcement,
  Download,
} from "@mui/icons-material";
import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Avatar,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  LinearProgress,
} from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import { useDispatch, useSelector } from "react-redux";
import AuthViewController from "../../redux/actions/AuthViewController";
import AttendanceVeiwController from "../../redux/actions/AttendenceApiAction";
import PayrollViewController from "../../redux/actions/payrollApiAction";
import LeaveRequestFormModal from "../../pages/Leaves/AddLeaveRequest";
import "./Dashboard.css";
import { Divider } from "@mui/material";
import { Stack } from "@mui/material";
import { deepPurple, green, orange, pink, grey } from "@mui/material/colors";
import { IMAGE_URL } from "../../services/api";
import ProtectedImage from "../../components/ProtectedImage";
import { Link } from "react-router-dom";
import PersonIcon from "@mui/icons-material/Person";
import CakeSharp from '@mui/icons-material/CakeSharp';


const getStatusColor = (status) => {
  switch (status.toLowerCase()) {
    case "completed":
      return "#4CAF50"; // Green
    case "pending":
      return "#90A4AE"; // Blue Grey
    case "in progress":
      return "#9575CD"; // Purple
    case "onhold":
      return "#F06292"; // Pink
    case "new":
      return "#29B6F6"; // Light Blue
    default:
      return "#BDBDBD"; // Default Grey
  }
};

const EmpDashboard = () => {
  const dispatch = useDispatch();
  const { getUserDetails, getDashboardData } = AuthViewController();
  const { addAttendance, updateAttendence } = AttendanceVeiwController();
  const { getPayslip } = PayrollViewController();
  const { userDetails, dashboardData } = useSelector((state) => state.auth);

  const [showModal, setShowModal] = React.useState(false);
  const [editLeave, setEditLeave] = React.useState(null);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    dispatch(getUserDetails());
    dispatch(getDashboardData());
  }, [dispatch]);

  const handleSignIn = () => {
    if (!userDetails?.id) return;
    const payload = {
      employee_id: userDetails.id,
      clock_in: new Date().toISOString(),
      date: new Date().toISOString().split("T")[0],
    };
    dispatch(addAttendance(payload))
      .then(() => dispatch(getDashboardData()))
      .catch((error) => console.error("Error during sign in:", error));
  };

  const handleSignOut = () => {
    if (!dashboardData?.attendance?.id) return;
    const payload = {
      id: dashboardData.attendance.id,
      clock_out: new Date().toISOString(),
      employee_id: userDetails.id,
    };
    dispatch(updateAttendence(payload))
      .then(() => dispatch(getDashboardData()))
      .catch((error) => console.error("Error during sign out:", error));
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const formatDuration = (start) => {
    if (!start) return "00:00:00";
    const diffMs = now - new Date(start);
    if (diffMs < 0) return "00:00:00";

    const totalSeconds = Math.floor(diffMs / 1000);
    const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, "0");
    const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(
      2,
      "0"
    );
    const seconds = String(totalSeconds % 60).padStart(2, "0");

    return `${hours}:${minutes}:${seconds}`;
  };

  const attendance = dashboardData?.attendance;

  const pieData = attendance
    ? [
        {
          name: "On Time",
          value: attendance.on_time_days || 0,
          color: "#0f172a",
        },
        {
          name: "Late Attendance",
          value: attendance.late_days || 0,
          color: "#22c55e",
        },
        { name: "Work From Home", value: 5, color: "#f97316" }, // static value
      ]
    : [];

  const today = new Date();

  const nextHoliday = dashboardData?.holiday_data || null;


const tomorrow = new Date();
tomorrow.setDate(today.getDate() + 1);

const combinedBirthdays = dashboardData?.upcoming_birthdays?.map((item) => {
  const birthdayDate = new Date(item.date_of_birth);
  const todayMonth = today.getMonth();
  const todayDate = today.getDate();

  let group = "future";

  if (
    birthdayDate.getDate() === todayDate &&
    birthdayDate.getMonth() === todayMonth
  ) {
    group = "today";
  } else if (
    birthdayDate.getDate() === tomorrow.getDate() &&
    birthdayDate.getMonth() === tomorrow.getMonth()
  ) {
    group = "tomorrow";
  }

  return { ...item, group };
}) || [];

const hasToday = combinedBirthdays.some((item) => item.group === "today");
const hasTomorrow = combinedBirthdays.some((item) => item.group === "tomorrow");
const hasFuture = combinedBirthdays.some((item) => item.group === "future");

const formatBirthday = (dob) => {
  const date = new Date(dob);
  const currentYear = new Date().getFullYear();
  // Use the original month/day but override year
  return new Date(currentYear, date.getMonth(), date.getDate())
    .toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
};


  return (
    <Box p={0} pb={3}>
      <Grid container spacing={3}>
        {/* Profile Card */}
        <Grid item md={4} xs={12}>
          <Card sx={{ height: "100%" }}>
            <CardHeader
              avatar={
                <Avatar sx={{ width: 50, height: 50, bgcolor: "primary.dark" }}>
                  <ProtectedImage
                    path={userDetails?.profile_data?.profile_image}
                    alt={`${userDetails?.first_name || ""} ${
                      userDetails?.last_name || ""
                    }`}
                    className="company-logo"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: "50%",
                    }}
                  />
                </Avatar>
              }
              title={`${userDetails?.first_name || ""} ${
                userDetails?.last_name || ""
              }`}
              subheader={userDetails?.profile_data?.designation_name || "N/A"}
              sx={{ bgcolor: "primary.dark", color: "white" }}
            />
            <CardContent>
              <Typography variant="body2">
                <strong>Phone Number:</strong>
                <br /> {userDetails?.mobile_number || "-"}
              </Typography>
              <Typography variant="body2" mt={2}>
                <strong>Email:</strong>
                <br />
                {userDetails?.email || "-"}
              </Typography>
              <Typography variant="body2" mt={2}>
                <strong>Report Office:</strong>
                <br />
                Pune{" "}
              </Typography>
              <Typography variant="body2" mt={2}>
                <strong>Joined on:</strong>
                <br />
                {userDetails?.profile_data?.joining_date
                  ? new Date(
                      userDetails.profile_data.joining_date
                    ).toLocaleDateString("en-GB", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                    })
                  : "-"}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item md={5} xs={12}>
          <Card sx={{ textAlign: "center", py: 3 }}>
            <Typography variant="subtitle2" color="textSecondary">
              Attendance
            </Typography>

            {/* Display Date and Time */}
            <Typography variant="h6" fontWeight="bold">
              {new Date().toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
              ,{" "}
              {new Date().toLocaleDateString(undefined, {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })}
            </Typography>

            {/* Total Hours */}
            <Box my={2} display="flex" justifyContent="center">
              <Box
                sx={{
                  width: 120,
                  height: 120,
                  borderRadius: "50%",
                  border: "8px solid #22c55e",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Typography variant="body2" color="textSecondary">
                  Total Hours
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {dashboardData?.attendance?.status === "signed_in"
                    ? formatDuration(dashboardData.attendance.clock_in)
                    : "00:00:00"}
                </Typography>
              </Box>
            </Box>

            {/* Production Hours */}
            <Box mt={1}>
              <Typography
                variant="body2"
                sx={{
                  background: "#0f172a",
                  color: "white",
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  display: "inline-block",
                }}
              >
                Production : 9:00 hrs
              </Typography>
            </Box>

            {/* Punch In Time (conditionally show) */}
            <Typography mt={2} variant="body2" color="textSecondary">
              <span role="img" aria-label="fingerprint">
                🔴
              </span>{" "}
              {dashboardData?.attendance?.clock_in
                ? `Punch In at ${new Date(
                    dashboardData.attendance.clock_in
                  ).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}`
                : "Not yet signed in"}
            </Typography>

            {/* Buttons */}
            <Stack direction="row" spacing={2} justifyContent="center" mt={2}>
              {/* Punch In Button */}
              <Button
                variant="contained"
                color="success"
                disabled={
                  !(
                    dashboardData?.attendance?.status === "not_signed_in" ||
                    dashboardData?.attendance?.status === "signed_out"
                  )
                }
                onClick={handleSignIn}
              >
                Punch In
              </Button>

              {/* Punch Out Button */}
              <Button
                variant="contained"
                color="warning"
                disabled={dashboardData?.attendance?.status !== "signed_in"}
                onClick={handleSignOut}
              >
                Punch Out
              </Button>
            </Stack>
          </Card>
        </Grid>

        {/* Leave Summary Card */}
        <Grid item md={3} xs={12}>
          <Card
            sx={{ height: "100%", display: "flex", flexDirection: "column" }}
          >
            <CardHeader title="Leave Balance" />
            <Divider sx={{ backgroundColor: "#555" }} />
            <CardContent
              sx={{ flexGrow: 1, display: "flex", flexDirection: "column" }}
            >
              <Box sx={{ flexGrow: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      Total Leaves
                      <br />
                      <strong>
                        {dashboardData?.leave_balance?.total_leave_allotted ||
                          "-"}
                      </strong>
                    </Typography>
                    <Typography variant="body2" mt={2}>
                      Absent
                      <br />
                      <strong>
                        {dashboardData?.leave_balance?.absent_days || "-"}
                      </strong>
                    </Typography>
                    <Typography variant="body2" mt={2}>
                      Worked Days
                      <br />
                      <strong>
                        {dashboardData?.leave_balance?.total_work_days || "-"}
                      </strong>
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2">
                      Taken
                      <br />
                      <strong>
                        {dashboardData?.leave_balance?.total_leave_approved ||
                          "-"}
                      </strong>
                    </Typography>
                    <Typography variant="body2" mt={2}>
                      Request
                      <br />
                      <strong>
                        {dashboardData?.leave_balance?.total_leave_requested ||
                          "-"}
                      </strong>
                    </Typography>
                    <Typography variant="body2" mt={2}>
                      Loss of Pay
                      <br />
                      <strong>2</strong>
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Button at bottom */}
              <Button
                fullWidth
                variant="contained"
                color="primary"
                sx={{ mt: 3 }}
                onClick={() => {
                  setEditLeave(null); // for new leave request
                  setShowModal(true);
                }}
              >
                Apply New Leave
              </Button>
            </CardContent>
          </Card>
        </Grid>
        {dashboardData?.announcements &&
          dashboardData.announcements.length > 0 && (
            <>
              <Grid item md={8} xs={12}>
                <Box height="100%">
                  <Card sx={{ p: 2, height: "100%" }}>
                    <Typography variant="h6" gutterBottom>
                      Announcements
                    </Typography>
                    {dashboardData?.announcements.map((item, index) => (
                      <Typography key={index} variant="body2">
                        • {item.title}
                      </Typography>
                    ))}
                  </Card>
                </Box>
              </Grid>

              {/* Small Card below Leave Balance */}
              <Grid item md={4} xs={12}>
                <Box height="100%">
                  <Card sx={{ p: 2, height: "100%" }}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Quick Note
                    </Typography>
                    <Typography variant="body2" mt={1}>
                      Team meeting at 4:00 PM. Don't forget to bring updates.
                    </Typography>
                  </Card>
                </Box>
              </Grid>
            </>
          )}
      </Grid>

      <Grid container spacing={3} mt={1} alignItems="stretch">
        <Grid
          item
          md={5}
          xs={12}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Box display="flex" flexDirection="column" gap={2}>
            {/* Attendance Details Card */}
            <Card>
              <CardHeader title="Attendance Details" />
              <Divider sx={{ backgroundColor: "#555" }} />
              <CardContent>
                <Box
                  display="flex"
                  flexDirection={{ xs: "column", md: "row" }}
                  gap={2}
                >
                  {/* Left Content */}
                  <Box width={{ xs: "100%", md: "50%" }}>
                    {pieData.map((entry, index) => (
                      <Typography
                        key={index}
                        variant="body2"
                        sx={{
                          color: entry.color,
                          fontWeight: "bold",
                          mb: index !== pieData.length - 1 ? 2 : 0,
                        }}
                      >
                        {entry.value}{" "}
                        <span style={{ color: "black", fontWeight: "normal" }}>
                          {entry.name}
                        </span>
                      </Typography>
                    ))}
                  </Box>

                  {/* Right Chart */}
                  <Box
                    width={{ xs: "100%", md: "50%" }}
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                  >
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie
                          data={pieData}
                          dataKey="value"
                          outerRadius={80}
                          innerRadius={50}
                          paddingAngle={5}
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Small Card Below */}
            <Card
              sx={{
                height: 100,
                backgroundColor: "#ffc107",
                p: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <CardContent sx={{ p: 0 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: "bold", color: "#1a237e" }}
                >
                  Next Holiday
                </Typography>

                {nextHoliday?.occasion ? (
                  <Typography
                    variant="body2"
                    title={`${nextHoliday.occasion} - ${nextHoliday.date}`}
                    sx={{ mt: 0.5, color: "#212121" }}
                  >
                    {nextHoliday.occasion.charAt(0).toUpperCase() +
                      nextHoliday.occasion.slice(1)}
                    ,{" "}
                    {new Date(nextHoliday.date).toLocaleDateString(undefined, {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </Typography>
                ) : (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    No upcoming holidays.
                  </Typography>
                )}
              </CardContent>
              <Link to="/holidays" style={{ textDecoration: "none" }}>
                <Box
                  sx={{
                    backgroundColor: "#fff",
                    color: "#000",
                    px: 2,
                    py: 1,
                    borderRadius: 1,
                    fontWeight: "bold",
                    textAlign: "center",
                    cursor: "pointer",
                    boxShadow: 1,
                    minWidth: 60,
                  }}
                >
                  View <br /> All
                </Box>
              </Link>
            </Card>
          </Box>
        </Grid>

        <Grid
          item
          md={4}
          xs={12}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Box display="flex" flexDirection="column" height="100%" gap={2}>
            {/* Row 1 */}
            <Box display="flex" gap={2} flex={1}>
              {/* Card 1 */}
              <Card sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                <CardContent
                  sx={{
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-start"
                    gap={0.5}
                  >
                    <Box
                      width={30}
                      height={30}
                      bgcolor="orange"
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      borderRadius={1}
                      color="white"
                    >
                      <Typography variant="caption">Q</Typography>
                    </Box>
                    <Box sx={{ wordBreak: "break-word", minWidth: 0 }}>
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        display="inline"
                      >
                        {
                          dashboardData?.weekly_work_hours?.current_week
                            ?.actual_work_hours
                        }
                      </Typography>
                      <Typography
                        variant="h6"
                        fontWeight="normal"
                        display="inline"
                        color="gray"
                      >
                        /{" "}
                        {
                          dashboardData?.weekly_work_hours?.current_week
                            ?.total_expected_hours
                        }
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" mt={2}>
                    Total Hours...
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mt={2}>
                    <Box
                      width={20}
                      height={20}
                      bgcolor={
                        dashboardData?.weekly_work_hours?.attendance_comparison
                          ?.status === "increased"
                          ? "green"
                          : "red"
                      }
                      borderRadius="50%"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Typography variant="caption" color="white">
                        {dashboardData?.weekly_work_hours?.attendance_comparison
                          ?.status === "increased"
                          ? "↑"
                          : "↓"}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      color={
                        dashboardData?.weekly_work_hours?.attendance_comparison
                          ?.status === "increased"
                          ? "green"
                          : "red"
                      }
                    >
                      {
                        dashboardData?.weekly_work_hours?.attendance_comparison
                          ?.percentage
                      }
                      % This Week
                    </Typography>
                  </Box>
                </CardContent>
              </Card>

              {/* Card 2 */}
              <Card sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                <CardContent
                  sx={{
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-start"
                    gap={0.5}
                  >
                    <Box
                      width={30}
                      height={30}
                      bgcolor="black"
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      borderRadius={1}
                      color="white"
                    >
                      <Typography variant="caption">Q</Typography>
                    </Box>
                    <Box sx={{ wordBreak: "break-word", minWidth: 0 }}>
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        display="inline"
                      >
                        {
                          dashboardData?.weekly_work_hours?.last_week
                            ?.actual_work_hours
                        }
                      </Typography>
                      <Typography
                        variant="h6"
                        fontWeight="normal"
                        display="inline"
                        color="gray"
                      >
                        {" / " +
                          dashboardData?.weekly_work_hours?.last_week
                            ?.total_expected_hours}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" mt={2}>
                    Total Hours...
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mt={2}>
                    <Box
                      width={20}
                      height={20}
                      bgcolor={
                        dashboardData?.weekly_work_hours?.last_week_comparison
                          ?.status === "increased"
                          ? "green"
                          : "red"
                      }
                      borderRadius="50%"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Typography variant="caption" color="white">
                        {dashboardData?.weekly_work_hours?.last_week_comparison
                          ?.status === "increased"
                          ? "↑"
                          : "↓"}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      color={
                        dashboardData?.weekly_work_hours?.last_week_comparison
                          ?.status === "increased"
                          ? "green"
                          : "red"
                      }
                    >
                      {
                        dashboardData?.weekly_work_hours?.last_week_comparison
                          ?.percentage
                      }
                      % Last Week
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>

            {/* Row 2 */}
            <Box display="flex" gap={2} flex={1}>
              {/* Card 3 */}
              <Card sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                <CardContent
                  sx={{
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-start"
                    gap={0.5}
                  >
                    <Box
                      width={30}
                      height={30}
                      bgcolor="blue"
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      borderRadius={1}
                      color="white"
                    >
                      <Typography variant="caption">Q</Typography>
                    </Box>
                    <Box whiteSpace="nowrap">
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        display="inline"
                      >
                        {
                          dashboardData?.monthly_work_data?.current_month
                            ?.actual_work_hours
                        }
                      </Typography>
                      <Typography
                        variant="h6"
                        fontWeight="normal"
                        display="inline"
                        color="gray"
                      >
                        {" "}
                        /{" "}
                        {
                          dashboardData?.monthly_work_data?.current_month
                            ?.total_expected_hours
                        }
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" mt={2}>
                    Total Hours...
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mt={2}>
                    <Box
                      width={20}
                      height={20}
                      bgcolor={
                        dashboardData?.monthly_work_data?.attendance_comparison
                          ?.status === "increased"
                          ? "green"
                          : "red"
                      }
                      borderRadius="50%"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Typography variant="caption" color="white">
                        {dashboardData?.monthly_work_data?.attendance_comparison
                          ?.status === "increased"
                          ? "↑"
                          : "↓"}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      color={
                        dashboardData?.monthly_work_data?.attendance_comparison
                          ?.status === "increased"
                          ? "green"
                          : "red"
                      }
                    >
                      {
                        dashboardData?.monthly_work_data?.attendance_comparison
                          ?.percentage
                      }
                      % Last Month
                    </Typography>
                  </Box>
                </CardContent>
              </Card>

              {/* Card 4 */}
              <Card sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
                <CardContent
                  sx={{
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="flex-start"
                    gap={0.5}
                  >
                    <Box
                      width={30}
                      height={30}
                      bgcolor="hotpink"
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                      borderRadius={1}
                      color="white"
                      flexShrink={0}
                    >
                      <Typography variant="caption">Q</Typography>
                    </Box>
                    <Box sx={{ minWidth: 0, wordBreak: "break-word" }}>
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        display="inline"
                      >
                        {
                          dashboardData?.monthly_work_data?.current_month
                            ?.overtime_hours
                        }
                      </Typography>
                      <Typography
                        variant="h6"
                        fontWeight="normal"
                        display="inline"
                        color="gray"
                      >
                        {" "}
                        /{" "}
                        {
                          dashboardData?.monthly_work_data?.current_month
                            ?.total_expected_hours
                        }
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" mt={2}>
                    Overtime this...
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1} mt={2}>
                    <Box
                      width={20}
                      height={20}
                      bgcolor={
                        dashboardData?.monthly_work_data?.overtime_comparison
                          ?.status === "increased"
                          ? "green"
                          : "red"
                      }
                      borderRadius="50%"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Typography variant="caption" color="white">
                        {dashboardData?.monthly_work_data?.overtime_comparison
                          ?.status === "increased"
                          ? "↑"
                          : "↓"}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      color={
                        dashboardData?.monthly_work_data?.overtime_comparison
                          ?.status === "increased"
                          ? "green"
                          : "red"
                      }
                    >
                      {
                        dashboardData?.monthly_work_data?.overtime_comparison
                          ?.percentage
                      }
                      % Last Month
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </Grid>

        {/* Payslip Card */}
        <Grid
          item
          md={3}
          xs={12}
          sx={{ display: "flex", flexDirection: "column" }}
        >
          <Box display="flex" flexDirection="column" gap={2}>
            {/* Pay Slip */}
            <Card
              sx={{ height: 205, display: "flex", flexDirection: "column" }}
            >
              <CardHeader
                title={
                  <span>
                    <MonetizationOnIcon
                      sx={{ mr: 1, verticalAlign: "middle" }}
                    />
                    Pay Slip
                  </span>
                }
                sx={{ pb: 0 }}
              />
              <Divider sx={{ backgroundColor: "#555" }} />
              {dashboardData?.payslip ? (
                <CardContent
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    pt: 1,
                  }}
                >
                  {/* Month & Net Salary Row */}
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        Month
                        <br />
                        <strong>{dashboardData?.payslip?.month || "-"}</strong>
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="body2">
                        Net Salary
                        <br />
                        <strong>
                          {dashboardData?.payslip?.net_salary || "-"}
                        </strong>
                      </Typography>
                    </Grid>
                  </Grid>

                  {/* Download Button */}
                  <Button
                    startIcon={<Download />}
                    fullWidth
                    variant="contained"
                    size="small"
                    onClick={() => {
                      const empId = userDetails?.id;
                      const payslipMonthStr = dashboardData?.payslip?.month;
                      if (!empId || !payslipMonthStr) return;

                      const date = new Date(payslipMonthStr + " 1");
                      const month = (date.getMonth() + 1)
                        .toString()
                        .padStart(2, "0");
                      const year = date.getFullYear().toString();
                      const EMP_id = userDetails?.employee_id;
                      
                      dispatch(getPayslip(empId, month, year,EMP_id));
                    }}
                  >
                    Download
                  </Button>
                </CardContent>
              ) : (
                <Typography
                  sx={{ p: 2, textAlign: "center" }}
                  color="text.secondary"
                >
                  No payslip available
                </Typography>
              )}
            </Card>

            {/* Birthday Card */}
    <Card
  sx={{
    height: 200,
    p: 2,
    overflowY: "auto",
  }}
>
  <Typography variant="subtitle2" color="textSecondary" mb={2}>
    🎉 Upcoming Birthday
  </Typography>

  {combinedBirthdays.length > 0 && (hasToday || hasTomorrow || hasFuture) ? (
    <>
      {hasToday && (
        <>
          <Typography variant="subtitle2" mb={1}>
            Today
          </Typography>
          {combinedBirthdays
            .filter((item) => item.group === "today")
            .map((item, index) => (
              <div
                key={`today-${index}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "12px",
                  padding: "8px",
                  border: "1px dashed #ddd",
                  borderRadius: "8px",
                  backgroundColor: "#f8f9fa",
                }}
              >
                {/* Date */}
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ width: 80, textAlign: "center" }}
                >
                  {new Date(item.date_of_birth).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                  })}
                </Typography>

                {/* Profile image */}
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    border: "2px solid #ddd",
                    bgcolor: "primary.dark",
                    marginRight: 2,
                  }}
                >
                  <ProtectedImage
                    path={item.profile_image}
                    alt={item.name || "Profile"}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: "50%",
                    }}
                  />
                </Avatar>

                {/* Name & Designation */}
                <div style={{ flex: 1 }}>
                  <Typography variant="body1" fontWeight={500} color="black">
                    {item.name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" fontWeight={200}>
                    {item.designation}
                  </Typography>
                </div>

          
              </div>
            ))}
        </>
      )}

      {hasTomorrow && (
        <>
          <Typography variant="subtitle2" mb={1}>
            Tomorrow
          </Typography>
          {combinedBirthdays
            .filter((item) => item.group === "tomorrow")
            .map((item, index) => (
              <div
                key={`tomorrow-${index}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "12px",
                  padding: "8px",
                  border: "1px dashed #ddd",
                  borderRadius: "8px",
                  backgroundColor: "#f8f9fa",
                }}
              >
                {/* Date */}
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ width: 80, textAlign: "center" }}
                >
                  {formatBirthday(item.date_of_birth)}
                </Typography>

                {/* Profile image */}
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    border: "2px solid #ddd",
                    bgcolor: "primary.dark",
                    marginRight: 2,
                  }}
                >
                  <ProtectedImage
                    path={item.profile_image}
                    alt={item.name || "Profile"}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: "50%",
                    }}
                  />
                </Avatar>

                {/* Name & Designation */}
                <div style={{ flex: 1 }}>
                  <Typography variant="body1" fontWeight={500} color="black">
                    {item.name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" fontWeight={200}>
                    {item.designation}
                  </Typography>
                </div>

                
              </div>
            ))}
        </>
      )}

      {hasFuture &&
        combinedBirthdays
          .filter((item) => item.group === "future")
          .map((item, index) => (
            <div key={`future-${index}`} style={{ marginBottom: "12px" }}>
              <Typography variant="body2" color="textSecondary" mb={1}>
                {formatBirthday(item.date_of_birth)}
              </Typography>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "8px",
                  border: "1px dashed #ddd",
                  borderRadius: "8px",
                  backgroundColor: "#f8f9fa",
                }}
              >
                <Avatar
                  sx={{
                    width: 48,
                    height: 48,
                    border: "2px solid #ddd",
                    bgcolor: "primary.dark",
                    marginRight: 2,
                  }}
                >
                  <ProtectedImage
                    path={item.profile_image}
                    alt={item.name || "Profile"}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: "50%",
                    }}
                  />
                </Avatar>
                <div style={{ flex: 1 }}>
                  <Typography variant="body1" fontWeight={500} color="black">
                    {item.name}
                  </Typography>
                  <Typography variant="body2" color="textSecondary"fontWeight={200}>
                    {item.designation}
                  </Typography>
                </div>
               
              </div>
            </div>
          ))}
    </>
  ) : (
    <Typography variant="body2" color="textSecondary" textAlign="center" py={4}>
      No Upcoming Birthday
    </Typography>
  )}
</Card>



          </Box>
        </Grid>
      </Grid>

      {/*  project Summary Card */}

      <Grid container spacing={3} mt={1}>
        {dashboardData?.projects?.length > 0 && (
          <Grid item md={6} xs={12}>
            <Card
              sx={{ height: "100%", display: "flex", flexDirection: "column" }}
            >
              <CardHeader
                title="Projects"
                action={
                  <Box
                    display="flex"
                    alignItems="center"
                    sx={{ cursor: "pointer" }}
                  >
                    <Typography
                      variant="body2"
                      color="text.primary"
                      sx={{ mr: 0.5 }}
                    >
                      Active Projects
                    </Typography>
                    <ExpandMoreIcon fontSize="small" />
                  </Box>
                }
              />
              <Divider sx={{ backgroundColor: "#555" }} />

              <Grid container spacing={2} sx={{ p: 2, pb: 2 }}>
                {(dashboardData?.projects || [])
                  .filter((proj) => proj.is_active)
                  .slice(0, 2)
                  .map((project, idx, arr) => (
                    <Grid
                      item
                      xs={12}
                      sm={arr.length === 1 ? 12 : 6} // ✅ full width if 1 project, half width if 2
                      key={idx}
                    >
                      <Card
                        variant="outlined"
                        sx={{ display: "flex", flexDirection: "column" }}
                      >
                        <CardHeader
                          action={<MoreVertIcon />}
                          sx={{
                            pb: 0,
                            display: "flex",
                            alignItems: "flex-start", // align icon top with title top
                            justifyContent: "space-between",
                            px: 2,
                            height: 56, // fixed header height (adjust as needed)
                            overflow: "hidden",
                          }}
                          title={
                            <Typography
                              sx={{
                                fontSize: "1rem",
                                fontWeight: 500,
                                color: "black",
                                display: "-webkit-box",
                                WebkitLineClamp: 2, // limit to 2 lines
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                lineHeight: 1.2,
                                maxHeight: "2.4em",
                                mr: 1,
                                flex: 1,
                              }}
                              title={project.title}
                            >
                              {project.title}
                            </Typography>
                          }
                        />
                        <Box display="flex" alignItems="center" px={2} pt={1}>
                          <Avatar sx={{ width: 32, height: 32, mr: 2 }}>
                            {project.project_leader_image ? (
                              <ProtectedImage
                                path={project.project_leader_image}
                                alt="Project Leader"
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                  borderRadius: "50%", // keeps it circular inside Avatar
                                }}
                              />
                            ) : (
                              <PersonIcon /> // fallback icon
                            )}
                          </Avatar>

                          <Box>
                            <Typography sx={{ color: "black" }}>
                              {project.project_leader_name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Project Leader
                            </Typography>
                          </Box>
                        </Box>

                        <Box
                          display="flex"
                          alignItems="center"
                          px={2}
                          pt={2}
                          pb={1}
                        >
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              bgcolor: "#ffbfae",
                              mr: 2,
                            }}
                          >
                            <CalendarMonthIcon fontSize="small" />
                          </Avatar>
                          <Box>
                            <Typography>
                              {new Date(project.end_date).toLocaleDateString(
                                "en-GB",
                                {
                                  day: "numeric",
                                  month: "short",
                                  year: "numeric",
                                }
                              )}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Deadline
                            </Typography>
                          </Box>
                        </Box>

                        <Box
                          px={2}
                          pb={2}
                          display="flex"
                          flexDirection="column"
                          gap={1.5}
                        >
                          <Box
                            sx={{
                              border: "1px solid #ccc",
                              borderRadius: 1,
                              px: 1.5,
                              py: 1,
                              display: "flex",
                              alignItems: "center",
                              backgroundColor: "#f9f9f9",
                            }}
                          >
                            <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                            <Typography variant="body2" noWrap>
                              Tasks:{" "}
                              <span style={{ color: "black" }}>
                                {project.completed_tasks}
                              </span>{" "}
                              / {project.total_tasks}
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              border: "1px solid #ccc",
                              borderRadius: 1,
                              px: 1.5,
                              py: 1,
                              display: "flex",
                              alignItems: "center",
                              backgroundColor: "#f9f9f9",
                            }}
                          >
                            <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                            <Typography variant="body2" noWrap>
                              Issues:{" "}
                              <span style={{ color: "black" }}>
                                {project.completed_issues}
                              </span>{" "}
                              / {project.total_issues}
                            </Typography>
                          </Box>
                        </Box>
                      </Card>
                    </Grid>
                  ))}
              </Grid>

              <CardContent sx={{ pt: 1, pb: 2 }} />
            </Card>
          </Grid>
        )}
        {dashboardData?.tasks?.length > 0 && (
          <Grid item md={6} xs={12}>
            <Card
              sx={{ height: "100%", display: "flex", flexDirection: "column" }}
            >
              <CardHeader
                title="Tasks"
                action={
                  <Box
                    display="flex"
                    alignItems="center"
                    sx={{ cursor: "pointer" }}
                  >
                    <Typography
                      variant="body2"
                      color="text.primary"
                      sx={{ mr: 0.5 }}
                    >
                      All Projects
                    </Typography>
                    <ExpandMoreIcon fontSize="small" />
                  </Box>
                }
              />
              <Divider sx={{ backgroundColor: "#555" }} />

              <Grid container spacing={2} sx={{ p: 2 }}>
                {(dashboardData?.tasks || []).slice(0, 4).map((task, idx) => (
                  <Grid item xs={12} key={idx}>
                    <Card
                      variant="outlined"
                      sx={{
                        p: 2,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <DragIndicatorIcon fontSize="small" />
                        <Typography
                          variant="body1"
                          sx={{
                            textDecoration:
                              task.status.toLowerCase() === "completed"
                                ? "line-through"
                                : "none",
                            fontWeight: 500,
                          }}
                        >
                          {task.title}
                        </Typography>
                      </Box>
                      <Chip
                        label={task.status}
                        size="small"
                        sx={{
                          backgroundColor: getStatusColor(task.status),
                          color: "#fff",
                          fontWeight: 500,
                          textTransform: "capitalize",
                        }}
                      />
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Card>
          </Grid>
        )}
      </Grid>

      <LeaveRequestFormModal
        show={showModal}
        handleClose={() => setShowModal(false)}
        initialData={editLeave}
      />
    </Box>
  );
};

export default EmpDashboard;
